import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, type KeyboardEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField, NumberField } from '@/components/forms';
import type { OrderCustomerOption } from '@/features/orders';
import { ORDER_STATUS_CHIP, listOrderCustomers, orderKeys } from '@/features/orders';
import { useCursorSelect } from '@/hooks/use-cursor-select';
import { appTzLabel, fromDate, toDayjs } from '@/lib/date';

import { listOrderSummaryCities } from '../api';
import { orderSummaryToDefaults, orderSummaryToFilters } from '../filters';
import { orderSummaryFiltersSchema } from '../schemas';
import type { OrderSummaryFilterFormValues, OrderSummaryListFilters } from '../types';

import styles from './order-summary-filter-drawer.module.scss';

/** Reuses the table's own label map, so a filter option can never drift from the chip the matching
 *  row renders. Lifecycle order (Order Placed → Shipped → Delivered → Cancelled), which is the
 *  map's key order and what every other order filter shows; the AC lists the same four states in a
 *  different order, which reads as an enumeration rather than a sequence. */
const STATUS_OPTIONS = (Object.keys(ORDER_STATUS_CHIP) as (keyof typeof ORDER_STATUS_CHIP)[]).map(
  (value) => ({ value, label: ORDER_STATUS_CHIP[value].label }),
);

const customerToOption = (c: OrderCustomerOption) => ({ value: c.id, label: c.name });
const cityToOption = (c: string) => ({ value: c, label: c });

/** Both meta endpoints return the cursor envelope nested under `meta`; `useCursorSelect` wants it
 *  flat. One shape adapter rather than the same three lines in each `fetchPage`. */
const flattenPage = <T,>(page: {
  data: T[];
  meta: { nextCursor?: string; hasNextPage: boolean };
}) => ({ data: page.data, nextCursor: page.meta.nextCursor, hasNextPage: page.meta.hasNextPage });

/** Cities are plain strings, so the option's value IS the wire value; the key still varies by search
 *  term, which is what resets `useCursorSelect` to the first page. Keyed UNDER `orderKeys.metaCities`
 *  rather than a `reports` namespace of its own — it is the orders endpoint, so this stays a child
 *  of that cache prefix and cannot drift into a second, separately-invalidated copy of it. */
const cityKey = (search: string) => [...orderKeys.metaCities(), search] as const;

/** AC12, half one: strip anything the field cannot hold out of a PASTE. antd's parser runs on every
 *  input event, so a pasted "12.34abc" is stripped in place rather than discarded whole (which is
 *  what an unparsed value does — it clears the field). */
const stripTo = (allowed: RegExp) => (raw?: string) => (raw ?? '').replace(allowed, '');

/** AC12, half two: reject a non-numeric character AS TYPED. Without this the character is visible
 *  until blur — antd only refuses to *commit* it. Modifier combos (ctrl/cmd+v, ctrl+a) and every
 *  non-printable key (Backspace, Tab, arrows) pass through untouched. */
const blockUnless =
  (allowed: RegExp) =>
  (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length > 1) return;
    if (!allowed.test(event.key)) {
      event.preventDefault();
      return;
    }
    // A second decimal point is as non-numeric as a letter — "1.2.3" parses to nothing and antd
    // silently truncates it on blur, which is worse than refusing the keystroke.
    if (event.key === '.' && event.currentTarget.value.includes('.')) event.preventDefault();
  };

/** Whole non-negative counts (No of Items): digits only — no sign, no decimal point. */
const COUNT_INPUT = {
  min: 0,
  precision: 0,
  parser: stripTo(/[^\d]/g),
  onKeyDown: blockUnless(/[0-9]/),
  style: { width: '100%' },
} as const;

/** Currency (Order Amount): the one field the AC allows two decimals on. No `prefix="$"` — the
 *  frame draws none (Figma 32:194755), unlike the Customer Detail order-history drawer. */
const AMOUNT_INPUT = {
  min: 0,
  precision: 2,
  parser: stripTo(/[^\d.]/g),
  onKeyDown: blockUnless(/[0-9.]/),
  style: { width: '100%' },
} as const;

type DateRangeFieldsetProps = {
  label: string;
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
};

/** One `<DateRangePicker>` per range (Figma 32:194752 / 74:265160), bound to the two scalar draft
 *  fields rather than a tuple — that is the shape the wire contract uses.
 *
 *  `allowEmpty` on both ends is what makes AC14/AC15's one-sided range possible: the two bounds are
 *  independent params, and the shared `toDayjsRange`/`fromDateRange` helpers would collapse a
 *  half-filled range to `null` (the bug ADO 102118 §12 hit), so each end maps on its own. */
function DateRangeFieldset({ label, from, to, onChange }: DateRangeFieldsetProps) {
  // Which end the admin is editing. `info.from` below only covers a single continuous pick; with a
  // range already applied, clicking straight into the To box starts a session with no picked start,
  // so without this the panel would disable nothing.
  //
  // A ref, NOT state: the picker is controlled off `value`, so a re-render between focus and Enter
  // re-applies that value and wipes whatever the admin has typed so far. The popup renders after
  // focus, so `disabledDate` always reads a current value.
  const activeEnd = useRef<'start' | 'end' | undefined>(undefined);

  // ADO 120504: a filter edge is an application-calendar (Eastern) date, so say so once here —
  // both ranges render through this fieldset.
  const tzLabel = appTzLabel(label);

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>{tzLabel}</legend>
      <DateRangePicker
        aria-label={tzLabel}
        format="MM/DD/YYYY"
        style={{ width: '100%' }}
        allowEmpty={[true, true]}
        onFocus={(_, info) => {
          activeEnd.current = info.range;
        }}
        // AC15: every date before the From is disabled for the To. antd does NOT give this for
        // free — rc-picker's default `order` silently REORDERS an inverted pick rather than
        // preventing it, so with no `disabledDate` the panel disables nothing.
        //
        // `info.from` is rc-picker's own range hint: the first date picked in the CURRENT
        // selection. It covers the fresh From→To flow but is undefined when a committed range is
        // reopened, so the committed From backs it up whenever the To input is the focused end.
        // Scoping to `activeEnd === 'end'` is what keeps the From freely re-pickable — an
        // unconditional bound would apply to both panels and trap the admin at their first choice.
        // (`info.type` is the panel mode — 'date'/'month' — not the range end.)
        disabledDate={(current, info) => {
          const start = info.from ?? (activeEnd.current === 'end' ? toDayjs(from) : undefined);
          return start != null && current.isBefore(start, 'day');
        }}
        value={[toDayjs(from) ?? null, toDayjs(to) ?? null]}
        onChange={(range) => onChange(fromDate(range?.[0]) ?? null, fromDate(range?.[1]) ?? null)}
      />
    </fieldset>
  );
}

export type OrderSummaryFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open (AC4). */
  value: OrderSummaryListFilters;
  onApply: (filters: OrderSummaryListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<OrderSummaryFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = orderSummaryToDefaults(value);
  const form = useForm<OrderSummaryFilterFormValues>({
    resolver: zodResolver(orderSummaryFiltersSchema),
    defaultValues: {
      ...draft,
      // The four date bounds are `enumField`s, so `toDefaults` yields the applied value or
      // `undefined`; coalesce each to `null` so the control is controlled from mount WHILE
      // preserving a re-seeded date. Hardcoding `null` would drop an applied range on reopen, and
      // leaving `undefined` makes RHF silently revert to the default.
      orderDateFrom: draft.orderDateFrom ?? null,
      orderDateTo: draft.orderDateTo ?? null,
      deliveryDateFrom: draft.deliveryDateFrom ?? null,
      deliveryDateTo: draft.deliveryDateTo ?? null,
    },
    mode: 'onBlur',
  });

  // AC11. Both dropdowns are cursor-paginated and server-searched: scroll the popup for the next
  // page, a new term restarts at the first page. Customers come from the SHIPPED orders meta call
  // (`?hasOrder=true`) — Reports owns no meta routes; cities from the orders city meta scoped to
  // shipping cities. Both are exactly "values that can appear in this report".
  const customerSelect = useCursorSelect({
    queryKey: orderKeys.metaCustomers,
    fetchPage: async (params) => flattenPage(await listOrderCustomers(params)),
    toOption: customerToOption,
  });

  const citySelect = useCursorSelect({
    queryKey: cityKey,
    fetchPage: async (params) => flattenPage(await listOrderSummaryCities(params)),
    toOption: cityToOption,
  });

  const [orderDateFrom, orderDateTo, deliveryDateFrom, deliveryDateTo] = useWatch({
    control: form.control,
    name: ['orderDateFrom', 'orderDateTo', 'deliveryDateFrom', 'deliveryDateTo'],
  });

  // AC13: Apply stays disabled while either numeric pair is inverted. Watched off the two `To`
  // errors specifically — `enforceRangePairs` attaches the issue there, and `<FormField>` renders
  // the message under the same field. The date pairs need no equivalent: the picker orders the pair
  // on submit, so an inverted one cannot exist (see `orderSummaryFiltersSchema`).
  const rangeInvalid = Boolean(form.formState.errors.amountTo || form.formState.errors.itemCountTo);

  const handleApply = form.handleSubmit((values) => {
    onApply(orderSummaryToFilters(values));
    onClose();
  });

  // No `width`: the shared Drawer already defaults to the Figma panel width (464px, node 32:194736).
  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={rangeInvalid}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="customerIds"
        control={form.control}
        label="Customers"
        placeholder="Select"
        showSearch={customerSelect.showSearch}
        loading={customerSelect.loading}
        onPopupScroll={customerSelect.onPopupScroll}
        options={customerSelect.options}
      />

      <MultiSelectField
        name="cities"
        control={form.control}
        label="City"
        placeholder="Select"
        showSearch={citySelect.showSearch}
        loading={citySelect.loading}
        onPopupScroll={citySelect.onPopupScroll}
        options={citySelect.options}
      />

      <DateRangeFieldset
        label="Order Date Range"
        from={orderDateFrom}
        to={orderDateTo}
        onChange={(from, to) => {
          form.setValue('orderDateFrom', from, { shouldValidate: true });
          form.setValue('orderDateTo', to, { shouldValidate: true });
        }}
      />

      <DateRangeFieldset
        label="Delivery Date Range"
        from={deliveryDateFrom}
        to={deliveryDateTo}
        onChange={(from, to) => {
          form.setValue('deliveryDateFrom', from, { shouldValidate: true });
          form.setValue('deliveryDateTo', to, { shouldValidate: true });
        }}
      />

      <MultiSelectField
        name="orderStatus"
        control={form.control}
        label="Order Status"
        placeholder="Select"
        options={STATUS_OPTIONS}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Order Amount</legend>
        <div className={styles.row}>
          <NumberField
            name="amountFrom"
            control={form.control}
            aria-label="Order Amount From"
            placeholder="From"
            {...AMOUNT_INPUT}
          />
          <NumberField
            name="amountTo"
            control={form.control}
            aria-label="Order Amount To"
            placeholder="To"
            {...AMOUNT_INPUT}
          />
        </div>
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>No of Items</legend>
        <div className={styles.row}>
          <NumberField
            name="itemCountFrom"
            control={form.control}
            aria-label="No of Items From"
            placeholder="From"
            {...COUNT_INPUT}
          />
          <NumberField
            name="itemCountTo"
            control={form.control}
            aria-label="No of Items To"
            placeholder="To"
            {...COUNT_INPUT}
          />
        </div>
      </fieldset>
    </Drawer>
  );
}

export function OrderSummaryFilterDrawer({ open, ...rest }: OrderSummaryFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // AC3/AC4: unmount on close, remount fresh on open — that is what discards an abandoned edit and
  // re-seeds the draft from the last applied `value`.
  return <FilterBody {...rest} />;
}
